import dayjs from "dayjs"
import { useLazyGetCallRecordsQuery } from "features/callRecording/api"
import { useLazyGetLeadsQuery, useLazyGetTaskLogsQuery } from "features/clients/detail/api"
import { useMemo } from "react"
import { localisedValue } from "utils"

const formatEntityCallLabel = item => {
  if (!item) return ""

  const tag = localisedValue({ object: item, key: "tagShort" })
  const date = item?.date ? dayjs(item.date).format("MMM D, YYYY") : ""

  let durationStr = ""
  if (item?.duration) {
    const [hh, mm, ss] = item.duration.split(":").map(Number)
    const totalMinutes = hh * 60 + mm + Math.round(ss / 60)
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60

    durationStr = hours > 0 ? `${hours} hr ${minutes} min` : `${minutes} min`
  }

  return `${tag} - ${date} (${durationStr})`
}

const formatEntityMeetingLabel = item => {
  if (!item) return ""

  const date = item?.createdAt ? dayjs(item?.createdAt).format("MMM D, YYYY") : ""

  let location = ""

  if (item?.AgencyOffice?.title) {
    location = localisedValue({ object: item.AgencyOffice, key: "title" })
  } else if (item?.TaskProject?.name) {
    location = localisedValue({ object: item.TaskProject, key: "name" })
  } else if (item?.TaskType) {
    location = localisedValue({ object: item.TaskType, key: "name" })
  }

  return `${location} - ${date}`
}

const reviewTaskTypeKeys =
  "meeting_done,site_office,company_office,outdoor,sale_event,pse_sale_event_meeting_done,property_gala_sale_event_meeting_done,site_office_sale_event_meeting_done,company_office_sale_event_meeting_done,walk_in,video_meeting,google_meet,zoom_call,whatsapp_video_call"

const apiPayloads = {
  lead: { sortBy: "createdAt", sortOrder: "desc", page: 0, pageLimit: 50 },
  call: {
    page: 0,
    pageLimit: 50,
  },
  meeting: {
    page: 0,
    pageLimit: 50,
    clientTaskAllocatedUsers: true,
    sortBy: "id",
    sortOrder: "desc",
    taskTypeKeys: reviewTaskTypeKeys,
  },
}

export const useEntityApis = ({ clientId }) => {
  const [getLeads, { data: leads, isFetching: isFetchingLeads }] = useLazyGetLeadsQuery()

  const [getCalls, { data: calls, isFetching: isFetchingCalls }] = useLazyGetCallRecordsQuery()

  const [getClientTaskLogs, { data: clientLeads, isFetching: isFetchingTasks }] = useLazyGetTaskLogsQuery()

  const entityApiMap = useMemo(
    () => ({
      lead: {
        call: () => getLeads({ ...apiPayloads.lead, clientId }, true),
        data: leads?.data,
        loading: isFetchingLeads,
        getOptionValue: opt => opt?.id,
        getOptionLabel: opt => opt?.id?.toString(),
        dataKey: "data"
      },

      call: {
        call: () => getCalls({ ...apiPayloads.call, clientId }, true),
        data: calls?.phoneCallRecords,
        loading: isFetchingCalls,
        getOptionValue: opt => opt?.id,
        getOptionLabel: opt => formatEntityCallLabel(opt),
        dataKey: "phoneCallRecords"
      },

      meeting: {
        call: () => getClientTaskLogs({ filterObject: apiPayloads.meeting, clientId }, true),
        data: clientLeads?.data,
        loading: isFetchingTasks,
        getOptionValue: opt => opt?.id,
        getOptionLabel: opt => formatEntityMeetingLabel(opt),
        dataKey: "data"
      },
    }),
    [
      clientId,
      leads,
      calls,
      clientLeads,
      isFetchingLeads,
      isFetchingCalls,
      isFetchingTasks,
      getLeads,
      getCalls,
      getClientTaskLogs,
    ]
  )

  return entityApiMap
}
